import React from "react";

import { Eyebrow, Title, ShieldIcon } from "../components/CommonComponents";
import MetaInfo from "../components/MetaInfo";
import Header from "../components/Header";
import Footer from "../components/Footer";

const managementSections = [
    {
        number: "01",
        id: "mission-vision",
        shortTitle: "Mission & Vision",
        label: "Institutional Direction",
        title: "Commitment to Mission and Vision",
        description: "Management is expected to align decisions, policies and institutional priorities with the University's purpose and values.",
        points: [
            "Demonstrate strong dedication to maintain the goal, vision, mission and values of the university",
            "Uphold ideals of academic superiority, honesty, social inclusiveness, and public service",
            "Align all decisions and policies with the university's core principles",
        ],
    },
    {
        number: "02",
        id: "ethical-behaviour",
        shortTitle: "Ethical Behaviour",
        label: "Integrity",
        title: "Ethical Behaviour",
        description: "Management decisions should be guided by honesty, fairness and the best interests of the University.",
        points: [
            "Exhibit honesty, integrity, and transparency in all activities and decisions",
            "Avoid any conflicts of interest and prioritize the university's best interests",
            "Maintain impartiality and fairness in all dealings",
        ],
    },
    {
        number: "03",
        id: "compliance",
        shortTitle: "Compliance",
        label: "Governance",
        title: "Compliance with Laws and Regulations",
        description: "University management must operate within relevant regulatory, academic and institutional frameworks.",
        points: [
            "Adhere to all relevant rules, regulations, and policies governing higher education",
            "Ensure compliance with academic standards, financial management, and equal opportunity policies",
            "Stay updated with changing legal and regulatory requirements",
        ],
    },
    {
        number: "04",
        id: "diversity-inclusion",
        shortTitle: "Diversity & Inclusion",
        label: "Inclusive Leadership",
        title: "Respect for Diversity and Inclusion",
        description: "A responsible management culture respects diversity, dignity and equal opportunity across the University.",
        points: [
            "Foster a diverse, inclusive, and dignified atmosphere",
            "Appreciate contributions from all individuals regardless of race, ethnicity, gender, religion, sexual orientation, or disability",
            "Implement policies that promote equal opportunity and prevent discrimination",
        ],
    },
    {
        number: "05",
        id: "professionalism",
        shortTitle: "Professionalism",
        label: "Leadership Conduct",
        title: "Professionalism and Collegiality",
        description: "Professional leadership depends on appropriate conduct, collaboration and mutual respect.",
        points: [
            "Demonstrate appropriate behaviour at all times",
            "Cultivate collegial relationships with faculty, staff, students, and stakeholders",
            "Encourage collaboration, teamwork, and mutual respect",
        ],
    },
    {
        number: "06",
        id: "resource-stewardship",
        shortTitle: "Resource Stewardship",
        label: "Institutional Resources",
        title: "Resource Stewardship",
        description: "University resources should be managed responsibly, efficiently and with long-term institutional interests in mind.",
        points: [
            "Responsibly manage university resources including finances, personnel, and physical assets",
            "Ensure efficient and effective utilization to achieve strategic objectives",
            "Implement sustainable practices for long-term institutional health",
        ],
    },
    {
        number: "07",
        id: "accountability",
        shortTitle: "Accountability",
        label: "Transparent Governance",
        title: "Accountability and Transparency",
        description: "Management is expected to take responsibility for decisions and maintain appropriate records and communication.",
        points: [
            "Take responsibility for actions and decisions",
            "Maintain clear communication and proper documentation",
            "Provide justification for significant decisions when appropriate",
        ],
    },
    {
        number: "08",
        id: "confidentiality",
        shortTitle: "Confidentiality",
        label: "Privacy",
        title: "Confidentiality and Privacy",
        description: "Sensitive institutional, student and personnel information must be handled responsibly.",
        points: [
            "Protect sensitive information including student records and personnel matters",
            "Disclose confidential information only when necessary and authorized",
            "Comply with all data protection regulations",
        ],
    },
    {
        number: "09",
        id: "conflict-management",
        shortTitle: "Conflict Management",
        label: "Fair Resolution",
        title: "Conflict Management",
        description: "Conflicts should be handled constructively, fairly and through transparent mechanisms.",
        points: [
            "Prevent and resolve conflicts in a constructive and fair manner",
            "Promote transparent communication and reconciliation",
            "Implement fair grievance redressal mechanisms",
        ],
    },
    {
        number: "10",
        id: "continuous-improvement",
        shortTitle: "Continuous Improvement",
        label: "Future Readiness",
        title: "Continuous Improvement",
        description: "Management should continuously strengthen its knowledge, systems and administrative practices.",
        points: [
            "Pursue professional development and learning opportunities",
            "Stay updated with effective methods in higher education administration",
            "Adapt to changing circumstances and emerging best practices",
        ],
    },
];

export default function CodeOfConductManagement() {
    return (
        <>
            <MetaInfo param="" id="about" />
            <Header school="common" />

            <main className="bg-white text-[#132337]">

                {/* HERO */}
                <section className="relative overflow-hidden bg-[#071A2F] pt-[100px] text-white">

                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute inset-y-0 right-0 w-[44%] bg-[#0C2744]" />
                        <div className="absolute right-0 top-0 h-full w-[44%] opacity-[0.045] [background-image:linear-gradient(rgba(255,255,255,.7)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.7)_1px,transparent_1px)] [background-size:54px_54px]" />
                        <div className="absolute right-[8%] top-[90px] h-[340px] w-[340px] rounded-full border border-white/[0.05]" />
                        <div className="absolute right-[12%] top-[140px] h-[240px] w-[240px] rounded-full border border-[#D5AD57]/10" />
                        <div className="absolute bottom-0 left-0 h-[5px] w-[30%] bg-[#8B1E3F]" />
                        <div className="absolute bottom-0 left-[30%] h-[5px] w-[15%] bg-[#D5AD57]" />
                    </div>

                    <div className="relative mx-auto grid min-h-[610px] w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)] lg:grid-cols-[1.06fr_.94fr]">

                        {/* LEFT */}
                        <div className="flex flex-col justify-center py-16 pr-8 lg:py-20 lg:pr-20">

                            <div className="flex items-center gap-4 text-sm font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                <span className="h-px w-12 bg-current" />
                                Management Code of Conduct
                            </div>

                            <h1 className="mt-7 max-w-[850px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(52px,6vw,88px)] font-normal leading-[0.96] tracking-[-0.045em]">
                                Leadership with purpose.
                                <span className="block text-[#D5AD57]">
                                    Governance with integrity.
                                </span>
                            </h1>

                            <p className="mt-8 max-w-[700px] text-[17px] leading-8 text-white/65">
                                A management code of conduct in a University portrays the anticipated conduct, obligations, values and moral criteria the management of the university authorities are expected to follow.
                            </p>

                            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4 border-t border-white/15 pt-7">

                                {[
                                    "Integrity",
                                    "Transparency",
                                    "Accountability",
                                    "Responsible Governance",
                                ].map((item) => (
                                    <div key={item} className="flex items-center gap-3">
                                        <span className="h-1.5 w-1.5 rotate-45 bg-[#D5AD57]" />
                                        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/65">{item}</span>
                                    </div>
                                ))}

                            </div>

                        </div>


                        {/* RIGHT */}
                        <div className="relative hidden border-l border-white/10 lg:flex lg:items-center">

                            <span className="absolute right-[-20px] top-[-40px] font-['Libre_Caslon_Display',Georgia,serif] text-[270px] font-normal leading-none tracking-[-0.08em] text-white/[0.03]">
                                10
                            </span>

                            <div className="relative w-full px-10 xl:px-14">

                                <div className="flex items-center justify-between">

                                    <div className="grid h-16 w-16 place-items-center rounded-full border border-[#D5AD57]/40 text-[#D5AD57]">
                                        <ShieldIcon />
                                    </div>

                                    <div className="text-right">
                                        <strong className="block font-['Libre_Caslon_Display',Georgia,serif] text-[58px] font-normal leading-none text-[#D5AD57]">
                                            10
                                        </strong>

                                        <span className="mt-2 block text-[9px] font-bold uppercase tracking-[0.16em] text-white/40">
                                            Governance Standards
                                        </span>
                                    </div>

                                </div>

                                <div className="mt-9">

                                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                        Management Responsibility Framework
                                    </span>

                                    <h2 className="mt-4 max-w-[450px] font-['Libre_Caslon_Display',Georgia,serif] text-[35px] font-normal leading-[1.15]">
                                        Principles for responsible institutional leadership.
                                    </h2>

                                </div>


                                <div className="mt-9 grid grid-cols-2 border-l border-t border-white/10">

                                    {managementSections.map((item) => (
                                        <div key={item.number} className="border-b border-r border-white/10 px-4 py-3.5">

                                            <span className="font-['Libre_Caslon_Display',Georgia,serif] text-[16px] text-[#D5AD57]">
                                                {item.number}
                                            </span>

                                            <p className="mt-2 text-[9px] font-bold uppercase leading-4 tracking-[0.08em] text-white/50">
                                                {item.shortTitle}
                                            </p>

                                        </div>
                                    ))}

                                </div>

                            </div>

                        </div>

                    </div>

                </section>


                {/* MANAGEMENT CONTENT */}
                <section className="relative bg-[#F7F2E8] py-20 sm:py-24 lg:py-28">

                    <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">

                        {/* HEADER */}
                        <div className="grid gap-10 border-b border-[#071A2F]/15 pb-11 lg:grid-cols-[1fr_440px] lg:items-end">

                            <div>
                                <Eyebrow>Management Code of Conduct</Eyebrow>

                                <Title before="Standards for" highlight="Responsible Governance" after="" position="middle" theme="burgundy" />
                            </div>

                            <p className="text-[15px] leading-8 text-[#65727E]">
                                The management code defines the ethical, professional and institutional responsibilities expected from University authorities and decision-makers.
                            </p>

                        </div>


                        {/* SIDEBAR + CONTENT */}
                        <div className="mt-14 grid gap-12 lg:grid-cols-[300px_minmax(0,1fr)] lg:items-start lg:gap-16">

                            {/* STICKY SIDEBAR */}
                            <aside className="sticky top-[145px] hidden self-start lg:block">

                                <div className="border border-[#071A2F]/10 bg-white">

                                    <div className="border-b border-[#071A2F]/10 p-6">

                                        <div className="flex items-center justify-between">

                                            <span className="text-[10px] font-bold uppercase tracking-[0.17em] text-[#8B1E3F]">
                                                Management Standards
                                            </span>

                                            <span className="font-['Libre_Caslon_Display',Georgia,serif] text-[23px] text-[#D5AD57]">
                                                10
                                            </span>

                                        </div>

                                        <p className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-[24px] leading-[1.2] text-[#071A2F]">
                                            Governance Framework
                                        </p>

                                    </div>


                                    <nav className="px-6">

                                        {managementSections.map((item) => (
                                            <a key={item.id} href={`#${item.id}`} className="group grid grid-cols-[34px_1fr_auto] items-center gap-3 border-b border-[#071A2F]/10 py-3.5 last:border-b-0">

                                                <span className="font-['Libre_Caslon_Display',Georgia,serif] text-[17px] text-[#D5AD57]">
                                                    {item.number}
                                                </span>

                                                <span className="text-[12px] font-semibold leading-5 text-[#071A2F]/60 transition group-hover:text-[#8B1E3F]">
                                                    {item.shortTitle}
                                                </span>

                                                <span className="text-[#8B1E3F]/25 transition group-hover:translate-x-1 group-hover:text-[#8B1E3F]">
                                                    →
                                                </span>

                                            </a>
                                        ))}

                                    </nav>

                                </div>


                                <div className="mt-5 bg-[#071A2F] p-6 text-white">

                                    <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#D5AD57]">
                                        Leadership Principle
                                    </span>

                                    <p className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-[24px] leading-[1.3]">
                                        Strong governance begins with responsible leadership.
                                    </p>

                                </div>

                            </aside>


                            {/* MAIN CONTENT */}
                            <div className="min-w-0">

                                <div className="mb-3 flex items-end justify-between border-b border-[#071A2F]/15 pb-7">

                                    <div>

                                        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                            Institutional Governance
                                        </span>

                                        <h2 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(36px,4vw,52px)] font-normal leading-[1.05] text-[#071A2F]">
                                            Management Responsibility Framework
                                        </h2>

                                    </div>

                                    <span className="hidden font-['Libre_Caslon_Display',Georgia,serif] text-[64px] leading-none text-[#D5AD57]/30 sm:block">
                                        01—10
                                    </span>

                                </div>


                                {/* POLICIES */}
                                <div className="relative">

                                    <div className="absolute bottom-0 left-[31px] top-[50px] hidden w-px bg-[#071A2F]/10 md:block" />

                                    {managementSections.map((section, index) => (
                                        <article key={section.id} id={section.id} className="relative scroll-mt-[160px] border-b border-[#071A2F]/15 py-10 first:pt-8">

                                            <div className="grid gap-7 md:grid-cols-[64px_230px_1fr]">

                                                {/* NUMBER */}
                                                <div className="relative z-10">

                                                    <span className={`grid h-16 w-16 place-items-center rounded-full border font-['Libre_Caslon_Display',Georgia,serif] text-[20px] ${index === 1 || index === 6 ? "border-[#8B1E3F] bg-[#8B1E3F] text-white" : "border-[#D5AD57] bg-[#F7F2E8] text-[#A77B2D]"}`}>
                                                        {section.number}
                                                    </span>

                                                </div>


                                                {/* TITLE */}
                                                <div>

                                                    <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                                        {section.label}
                                                    </span>

                                                    <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-[28px] font-normal leading-[1.12] text-[#071A2F]">
                                                        {section.title}
                                                    </h3>

                                                </div>


                                                {/* CONTENT */}
                                                <div>

                                                    <p className="mb-5 text-[14px] leading-7 text-[#65727E]">
                                                        {section.description}
                                                    </p>

                                                    <ul className="border-t border-[#071A2F]/10">

                                                        {section.points.map((point, pointIndex) => (
                                                            <li key={pointIndex} className="grid grid-cols-[24px_1fr] gap-4 border-b border-[#071A2F]/10 py-4">

                                                                <span className="mt-[9px] h-1.5 w-1.5 rotate-45 bg-[#D5AD57]" />

                                                                <span className="text-[14px] leading-7 text-[#52606C]">
                                                                    {point}
                                                                </span>

                                                            </li>
                                                        ))}

                                                    </ul>

                                                </div>

                                            </div>

                                        </article>
                                    ))}

                                </div>


                                {/* NOTE */}
                                <article className="mt-12 overflow-hidden bg-[#8B1E3F] text-white">

                                    <div className="grid lg:grid-cols-[250px_1fr]">

                                        <div className="border-b border-white/10 p-8 lg:border-b-0 lg:border-r lg:p-10">

                                            <span className="text-[9px] font-bold uppercase tracking-[0.17em] text-[#D5AD57]">
                                                Institutional Review
                                            </span>

                                            <strong className="mt-10 block font-['Libre_Caslon_Display',Georgia,serif] text-[56px] font-normal leading-none text-[#D5AD57]">
                                                Note
                                            </strong>

                                        </div>


                                        <div className="p-8 lg:p-10">

                                            <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-[31px] font-normal leading-[1.15]">
                                                Continuous review supports institutional integrity.
                                            </h3>

                                            <p className="mt-5 text-[15px] leading-8 text-white/70">
                                                Universities must perform regular reviews and updates of their code of conduct for management to ensure that it remains relevant and effective in fostering ethical behaviour and maintaining institutional integrity. In addition, training and support programmes may be offered to assist management professionals in comprehending and adhering to the concepts portrayed in the code of conduct.
                                            </p>

                                        </div>

                                    </div>

                                </article>

                            </div>

                        </div>

                    </div>

                </section>


                {/* CLOSING */}
                <section className="bg-[#071A2F] text-white">

                    <div className="mx-auto grid w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)] lg:grid-cols-[.72fr_1.28fr]">

                        <div className="border-b border-white/10 py-16 lg:border-b-0 lg:border-r lg:py-20 lg:pr-14">

                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                University Leadership
                            </span>

                            <h2 className="mt-5 font-['Libre_Caslon_Display',Georgia,serif] text-[38px] font-normal leading-[1.1]">
                                Governance that reflects institutional values.
                            </h2>

                        </div>


                        <div className="flex items-center py-16 lg:py-20 lg:pl-16">

                            <p className="max-w-[760px] text-[16px] leading-8 text-white/60">
                                Ethical leadership, transparency, accountability and responsible stewardship together support the long-term academic and institutional strength of the University.
                            </p>

                        </div>

                    </div>

                </section>

            </main>

            <Footer />
        </>
    );
}